import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useLocation } from 'react-router-dom';

const Overview = () => {
  const { user } = useAuth();
  const { projectId } = useParams(); // For admin view
  const location = useLocation();
  const isAdminView = location.pathname.startsWith('/admin');

  const [project, setProject] = useState(null);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [nextMilestone, setNextMilestone] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    status: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchOverviewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectId]);

  const fetchOverviewData = async () => {
    try {
      // For admin: fetch by projectId param, for client: fetch by user.id
      let projectData;
      if (isAdminView && projectId) {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();
        if (error) throw error;
        projectData = data;
      } else {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('client_id', user.id)
          .single();
        if (error) throw error;
        projectData = data;
      }

      setProject(projectData);

      // Set edit form data
      setEditForm({
        name: projectData.name || '',
        description: projectData.description || '',
        status: projectData.status || 'discovery',
        start_date: projectData.start_date || '',
        end_date: projectData.end_date || ''
      });

      // Fetch current phase
      const { data: phaseData, error: phaseError } = await supabase
        .from('timeline_phases')
        .select('*')
        .eq('project_id', projectData.id)
        .eq('status', 'in-progress')
        .single();

      if (!phaseError) setCurrentPhase(phaseData);

      // Fetch next upcoming milestone
      const { data: milestoneData, error: milestoneError } = await supabase
        .from('timeline_milestones')
        .select('*')
        .eq('project_id', projectData.id)
        .eq('status', 'upcoming')
        .order('due_date', { ascending: true })
        .limit(1)
        .single();

      if (!milestoneError) setNextMilestone(milestoneData);

      // Fetch recent activity
      const { data: activityData, error: activityError } = await supabase
        .from('activity_log')
        .select('*')
        .eq('project_id', projectData.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (!activityError) setRecentActivity(activityData);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'discovery': 'bg-blue-100 text-blue-800',
      'design': 'bg-purple-100 text-purple-800',
      'development': 'bg-yellow-100 text-yellow-800',
      'testing': 'bg-orange-100 text-orange-800',
      'deployment': 'bg-green-100 text-green-800',
      'completed': 'bg-gray-100 text-gray-800',
      'on-hold': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('projects')
        .update(editForm)
        .eq('id', project.id);

      if (error) throw error;

      setProject({ ...project, ...editForm });
      setIsEditing(false);

      // Log activity
      await supabase.from('activity_log').insert([{
        project_id: project.id,
        user_id: user.id,
        action: 'project_updated',
        description: 'Project details updated'
      }]);

      // Refresh data
      fetchOverviewData();
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          {project?.name || 'Your Project'}
        </h1>
        <p className="text-gray-600">Welcome back! Here's what's happening with your project.</p>
      </motion.div>

      {/* Project Status Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="bg-white border border-gray-200 rounded-2xl p-6 mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Project Status</h2>
          <div className="flex items-center gap-3">
            {isAdminView && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-sm font-medium transition-colors"
              >
                {isEditing ? 'Cancel' : 'Edit Project'}
              </button>
            )}
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(project?.status)}`}>
              {project?.status?.replace('-', ' ').toUpperCase()}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Start Date</p>
            <p className="text-lg font-semibold text-gray-900">{formatDate(project?.start_date)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Expected Completion</p>
            <p className="text-lg font-semibold text-gray-900">{formatDate(project?.end_date)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Project Manager</p>
            <p className="text-lg font-semibold text-gray-900">AppTek Team</p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Phase */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white border border-gray-200 rounded-2xl p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Current Phase</h2>
          {currentPhase ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{currentPhase.name}</h3>
                <span className="text-sm text-gray-600">{currentPhase.progress_percentage || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className="bg-black h-2 rounded-full transition-all duration-300"
                  style={{ width: `${currentPhase.progress_percentage || 0}%` }}
                />
              </div>
              <p className="text-gray-600 mb-4">{currentPhase.description}</p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Started: {formatDate(currentPhase.start_date)}</span>
                <span className="text-gray-600">Due: {formatDate(currentPhase.end_date)}</span>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">No active phase at the moment.</p>
          )}
        </motion.div>

        {/* Next Milestone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white border border-gray-200 rounded-2xl p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Next Milestone</h2>
          {nextMilestone ? (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{nextMilestone.name}</h3>
              <p className="text-gray-600 mb-4">{nextMilestone.description}</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Due Date</p>
                  <p className="text-lg font-semibold text-gray-900">{formatDate(nextMilestone.due_date)}</p>
                </div>
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-600">No upcoming milestones scheduled.</p>
          )}
        </motion.div>
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-white border border-gray-200 rounded-2xl p-6 mt-6"
      >
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h2>
        {recentActivity.length > 0 ? (
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={activity.id} className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0">
                <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-gray-900">{activity.activity_type}: {activity.description}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(activity.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No recent activity to display.</p>
        )}
      </motion.div>

      {/* Edit Project Modal */}
      <AnimatePresence>
        {isEditing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setIsEditing(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Project</h3>
              <form onSubmit={handleUpdateProject}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Project Name</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      rows="4"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="discovery">Discovery</option>
                      <option value="design">Design</option>
                      <option value="development">Development</option>
                      <option value="testing">Testing</option>
                      <option value="deployment">Deployment</option>
                      <option value="completed">Completed</option>
                      <option value="on-hold">On Hold</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={editForm.start_date}
                        onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Completion</label>
                      <input
                        type="date"
                        value={editForm.end_date}
                        onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-black hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Overview;
